import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  type Column,
  useToast,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useLeafletActions,
  useLeafletMedicine,
  useLeafletVersions,
  useLeaflets,
} from '@/features/leaflets/api';
import {
  emptyLeafletContent,
  isLeafletComplete,
  LEAFLET_FIELDS,
  LEAFLET_LANGUAGES,
  LEAFLET_TEXT_MAX,
  type LeafletContent,
  type LeafletMedicine,
  type LeafletState,
  type LeafletVersion,
} from '@/features/leaflets/types';
import { isApiError } from '@/lib/api';
import { PageContainer } from '@/layouts/PageContainer';
import styles from './MedicineLeafletsPage.module.css';

type StateFilter = 'all' | LeafletState;

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const sameContent = (a: LeafletContent, b: LeafletContent) =>
  JSON.stringify(a) === JSON.stringify(b);

/** RL-1301: write, approve and retire the EN/BM leaflet behind each label QR. */
export function MedicineLeafletsPage() {
  const { t } = useTranslation();
  const [state, setState] = useState<StateFilter>('all');
  const [draftSearch, setDraftSearch] = useState('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const leaflets = useLeaflets({
    search: search || undefined,
    state: state === 'all' ? undefined : state,
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setSearch(draftSearch.trim());
  };
  const resetFilters = () => {
    setDraftSearch('');
    setSearch('');
    setState('all');
  };

  const columns: Column<LeafletMedicine>[] = [
    {
      key: 'medicine',
      header: t('leaflets.medicine'),
      render: (m) => (
        <>
          <span className={styles.primaryText}>{m.name}</span>
          <span className={styles.secondaryText}>{m.generic}</span>
        </>
      ),
    },
    {
      key: 'state',
      header: t('leaflets.state'),
      render: (m) => (
        <div className={styles.badges}>
          {m.approved && (
            <Badge tone="success">
              {t('leaflets.approvedBadge', { version: m.approved.version })}
            </Badge>
          )}
          {m.draft && (
            <Badge tone="warning">
              {t('leaflets.draftBadge', { version: m.draft.version })}
            </Badge>
          )}
          {!m.approved && !m.draft && (
            <Badge tone="neutral">{t('leaflets.noLeaflet')}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'lastChange',
      header: t('leaflets.lastChange'),
      render: (m) => {
        const latest = m.draft ?? m.approved;
        return latest ? (
          <span className={styles.secondaryText}>
            {t('leaflets.changeMeta', {
              when: when(latest.updatedAt),
              who: latest.updatedBy,
            })}
          </span>
        ) : (
          <span className={styles.secondaryText}>
            {t('common.notAvailable')}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">{t('leaflets.actions')}</span>,
      align: 'right',
      render: (m) => (
        <div className={styles.rowActions}>
          <Button
            size="sm"
            aria-label={`${t('leaflets.open')} ${m.name}`}
            onClick={() => setSelectedId(m.medicineId)}
          >
            {t('leaflets.open')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageContainer
      actions={
        <ChipToggle<StateFilter>
          label={t('leaflets.stateFilter')}
          single
          selected={[state]}
          options={(['all', 'approved', 'draft', 'none'] as const).map(
            (value) => ({ value, label: t(`leaflets.states.${value}`) }),
          )}
          onChange={(next) => {
            if (next[0]) setState(next[0]);
          }}
        />
      }
    >
      <Card title={t('leaflets.title')} flush>
        <form className={styles.filters} onSubmit={applyFilters}>
          <Field label={t('leaflets.searchLabel')}>
            <Input
              value={draftSearch}
              placeholder={t('leaflets.searchPlaceholder')}
              onChange={(event) => setDraftSearch(event.target.value)}
            />
          </Field>
          <div className={styles.filterActions}>
            <Button type="submit" variant="primary">
              {t('leaflets.applyFilters')}
            </Button>
            <Button type="button" variant="ghost" onClick={resetFilters}>
              {t('leaflets.clearFilters')}
            </Button>
          </div>
        </form>
        <DataTable
          caption={t('leaflets.title')}
          columns={columns}
          rows={leaflets.data}
          rowKey={(m) => m.medicineId}
          loading={leaflets.isLoading}
          error={leaflets.error}
          onRetry={() => void leaflets.refetch()}
          empty={
            <EmptyState
              title={t('leaflets.noMedicines')}
              body={t('leaflets.noMedicinesBody')}
            />
          }
        />
      </Card>

      {selectedId && (
        <LeafletDrawer
          medicineId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </PageContainer>
  );
}

function LeafletDrawer({
  medicineId,
  onClose,
}: {
  medicineId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detail = useLeafletMedicine(medicineId);
  const versions = useLeafletVersions(medicineId);
  const medicine = detail.data;

  return (
    <Dialog
      open
      wide
      title={medicine?.name ?? t('leaflets.title')}
      onClose={onClose}
    >
      {detail.isLoading ? (
        <LoadingState label={t('leaflets.loadingDetail')} />
      ) : detail.error || !medicine ? (
        <ErrorState
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      ) : (
        <div className={styles.drawerBody}>
          <LeafletOverview medicine={medicine} />
          <LeafletSections medicine={medicine} onClosed={onClose} />
          <section className={styles.section} aria-busy={versions.isFetching}>
            <h3>{t('leaflets.history')}</h3>
            {versions.isFetching && !versions.isLoading && (
              <p className={styles.muted} role="status">
                {t('leaflets.historyUpdating')}
              </p>
            )}
            {versions.isLoading ? (
              <LoadingState />
            ) : versions.error ? (
              <ErrorState
                error={versions.error}
                onRetry={() => void versions.refetch()}
              />
            ) : versions.data && versions.data.length > 0 ? (
              <ol className={styles.history}>
                {versions.data.map((v) => (
                  <HistoryRow key={v.id} version={v} />
                ))}
              </ol>
            ) : (
              <p className={styles.muted}>{t('leaflets.historyEmpty')}</p>
            )}
          </section>
        </div>
      )}
    </Dialog>
  );
}

function LeafletOverview({ medicine }: { medicine: LeafletMedicine }) {
  const { t } = useTranslation();
  return (
    <dl className={styles.facts}>
      <dt>{t('leaflets.genericName')}</dt>
      <dd>{medicine.generic}</dd>
      <dt>{t('leaflets.qrAddress')}</dt>
      <dd className={medicine.qrUrl ? styles.mono : undefined}>
        {medicine.qrUrl ?? t('leaflets.qrNone')}
      </dd>
    </dl>
  );
}

function HistoryRow({ version }: { version: LeafletVersion }) {
  const { t } = useTranslation();
  return (
    <li>
      <span className={styles.primaryText}>
        {t('leaflets.historyRow', {
          version: version.version,
          status: t(`leaflets.versionStatuses.${version.status}`),
        })}
      </span>
      <span className={styles.secondaryText}>
        {t('leaflets.historyEdited', {
          when: when(version.updatedAt),
          who: version.updatedBy,
        })}
      </span>
      {version.approvedAt && version.approvedBy && (
        <span className={styles.secondaryText}>
          {t('leaflets.historyApproved', {
            when: when(version.approvedAt),
            who: version.approvedBy,
          })}
        </span>
      )}
    </li>
  );
}

type RetireTarget = { leafletId: string; kind: 'approved' | 'draft' };

function LeafletSections({
  medicine,
  onClosed,
}: {
  medicine: LeafletMedicine;
  onClosed: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useSession();
  const canManage = hasPermission('medicine_leaflets:manage');
  const canApprove = hasPermission('medicine_leaflets:approve');
  const actions = useLeafletActions();
  const [retiring, setRetiring] = useState<RetireTarget | null>(null);
  const { approved, draft } = medicine;

  const startDraft = () =>
    actions.createDraft.mutate(
      {
        medicineId: medicine.medicineId,
        content: approved?.content ?? emptyLeafletContent(),
      },
      {
        onSuccess: () =>
          toast.show({ tone: 'success', title: t('leaflets.startedToast') }),
      },
    );

  const confirmRetire = () => {
    if (!retiring) return;
    const { leafletId, kind } = retiring;
    actions.retire.mutate(leafletId, {
      onSuccess: () => {
        toast.show({
          tone: 'success',
          title: t(
            kind === 'approved'
              ? 'leaflets.retiredToast'
              : 'leaflets.discardedToast',
          ),
        });
        setRetiring(null);
        // Nothing left to show if neither version remains.
        if (kind === 'approved' && !draft) onClosed();
      },
    });
  };

  return (
    <>
      {!canManage && !canApprove && (
        <p className={styles.notice}>{t('leaflets.readOnlyNotice')}</p>
      )}

      <section className={styles.section}>
        <h3>{t('leaflets.published')}</h3>
        {approved ? (
          <>
            <p className={styles.muted}>
              {t('leaflets.publishedMeta', {
                version: approved.version,
                when: approved.approvedAt ? when(approved.approvedAt) : '',
                who: approved.approvedBy ?? '',
              })}
            </p>
            <LeafletReadOnly content={approved.content} />
            {canApprove && (
              <div className={styles.actionBar}>
                <Button
                  variant="danger"
                  onClick={() => {
                    actions.retire.reset();
                    setRetiring({ leafletId: approved.id, kind: 'approved' });
                  }}
                >
                  {t('leaflets.retireLeaflet')}
                </Button>
              </div>
            )}
          </>
        ) : (
          <p className={styles.muted}>{t('leaflets.publishedNone')}</p>
        )}
      </section>

      <section className={styles.section}>
        <h3>{t('leaflets.draftSection')}</h3>
        {draft ? (
          <DraftEditor
            key={`${draft.id}:${draft.updatedAt}`}
            draft={draft}
            canManage={canManage}
            canApprove={canApprove}
            onDiscard={() => {
              actions.retire.reset();
              setRetiring({ leafletId: draft.id, kind: 'draft' });
            }}
          />
        ) : (
          <>
            <p className={styles.muted}>{t('leaflets.draftNone')}</p>
            {canManage && (
              <>
                {approved && (
                  <p className={styles.hint}>{t('leaflets.startDraftHint')}</p>
                )}
                <div className={styles.actionBar}>
                  <Button
                    variant="primary"
                    loading={actions.createDraft.isPending}
                    onClick={startDraft}
                  >
                    {approved
                      ? t('leaflets.startNewVersion')
                      : t('leaflets.startDraft')}
                  </Button>
                </div>
                {actions.createDraft.error && (
                  <ErrorState
                    error={actions.createDraft.error}
                    onRetry={null}
                  />
                )}
              </>
            )}
          </>
        )}
      </section>

      {retiring && (
        <Dialog
          open
          variant="modal"
          title={t(
            retiring.kind === 'approved'
              ? 'leaflets.retireApprovedTitle'
              : 'leaflets.discardTitle',
          )}
          onClose={() => setRetiring(null)}
          dismissible={!actions.retire.isPending}
          footer={
            <>
              <Button
                onClick={() => setRetiring(null)}
                disabled={actions.retire.isPending}
              >
                {t('leaflets.keep')}
              </Button>
              <Button
                variant="danger"
                loading={actions.retire.isPending}
                onClick={confirmRetire}
              >
                {t(
                  retiring.kind === 'approved'
                    ? 'leaflets.confirmRetire'
                    : 'leaflets.confirmDiscard',
                )}
              </Button>
            </>
          }
        >
          <p className={styles.muted}>
            {t(
              retiring.kind === 'approved'
                ? 'leaflets.retireApprovedBody'
                : 'leaflets.discardBody',
            )}
          </p>
          {actions.retire.error && (
            <ErrorState error={actions.retire.error} onRetry={null} />
          )}
        </Dialog>
      )}
    </>
  );
}

function LeafletReadOnly({ content }: { content: LeafletContent }) {
  const { t } = useTranslation();
  return (
    <div className={styles.languages}>
      {LEAFLET_LANGUAGES.map((language) => (
        <div key={language} className={styles.language}>
          <h4>{t(`leaflets.languages.${language}`)}</h4>
          {LEAFLET_FIELDS.map((field) => (
            <div key={field}>
              <span className="eyebrow">{t(`leaflets.fields.${field}`)}</span>
              <p className={styles.readText}>{content[language][field]}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function DraftEditor({
  draft,
  canManage,
  canApprove,
  onDiscard,
}: {
  draft: LeafletVersion;
  canManage: boolean;
  canApprove: boolean;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const actions = useLeafletActions();
  const [content, setContent] = useState<LeafletContent>(draft.content);
  const dirty = !sameContent(content, draft.content);
  const tooLong = LEAFLET_LANGUAGES.some((language) =>
    LEAFLET_FIELDS.some(
      (field) => content[language][field].length > LEAFLET_TEXT_MAX,
    ),
  );
  const complete = isLeafletComplete(content);
  const approveHint = dirty
    ? t('leaflets.saveFirst')
    : !complete
      ? t('leaflets.fillAll')
      : t('leaflets.approveHint');

  const approveError = actions.approve.error;
  const selfApproval =
    isApiError(approveError) && approveError.code === 'LEAFLET_SELF_APPROVAL';

  return (
    <>
      <p className={styles.muted}>
        {t('leaflets.draftMeta', {
          version: draft.version,
          when: when(draft.updatedAt),
          who: draft.updatedBy,
        })}
      </p>
      <div className={styles.languages}>
        {LEAFLET_LANGUAGES.map((language) => (
          <div key={language} className={styles.language}>
            <h4>{t(`leaflets.languages.${language}`)}</h4>
            {LEAFLET_FIELDS.map((field) => {
              const value = content[language][field];
              return (
                <Field
                  key={field}
                  label={t('leaflets.fieldLabel', {
                    field: t(`leaflets.fields.${field}`),
                    language: t(`leaflets.languages.${language}`),
                  })}
                  hint={t('leaflets.charCount', {
                    count: value.length,
                    max: LEAFLET_TEXT_MAX,
                  })}
                  error={
                    value.length > LEAFLET_TEXT_MAX
                      ? t('leaflets.fieldTooLong', { max: LEAFLET_TEXT_MAX })
                      : undefined
                  }
                >
                  <textarea
                    className={styles.textarea}
                    value={value}
                    readOnly={!canManage}
                    onChange={(event) => {
                      actions.saveDraft.reset();
                      actions.approve.reset();
                      setContent((current) => ({
                        ...current,
                        [language]: {
                          ...current[language],
                          [field]: event.target.value,
                        },
                      }));
                    }}
                  />
                </Field>
              );
            })}
          </div>
        ))}
      </div>

      {canApprove && <p className={styles.hint}>{approveHint}</p>}

      <div className={styles.actionBar}>
        {canManage && (
          <Button
            variant="primary"
            disabled={!dirty || tooLong}
            loading={actions.saveDraft.isPending}
            onClick={() =>
              actions.saveDraft.mutate(
                { leafletId: draft.id, content },
                {
                  onSuccess: () =>
                    toast.show({
                      tone: 'success',
                      title: t('leaflets.savedToast'),
                    }),
                },
              )
            }
          >
            {t('leaflets.saveDraft')}
          </Button>
        )}
        {canApprove && (
          <Button
            disabled={dirty || !complete || tooLong}
            loading={actions.approve.isPending}
            onClick={() =>
              actions.approve.mutate(draft.id, {
                onSuccess: () =>
                  toast.show({
                    tone: 'success',
                    title: t('leaflets.approvedToast'),
                  }),
              })
            }
          >
            {t('leaflets.approve')}
          </Button>
        )}
        {canApprove && (
          <Button variant="danger" onClick={onDiscard}>
            {t('leaflets.discardDraft')}
          </Button>
        )}
      </div>

      {actions.saveDraft.error && (
        <ErrorState error={actions.saveDraft.error} onRetry={null} />
      )}
      {approveError && (
        <ErrorState
          error={approveError}
          title={selfApproval ? t('leaflets.selfApprovalTitle') : undefined}
          body={selfApproval ? t('leaflets.selfApprovalBody') : undefined}
          onRetry={null}
        />
      )}
    </>
  );
}
